import { useState } from "react";
import { Edit3, Save, X } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { API } from "@/lib/api";

const client = axios.create({ baseURL: API, withCredentials: false });
client.interceptors.request.use((config) => {
  const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});
client.interceptors.request.use((c) => {
  const tok = typeof window !== "undefined" ? window.localStorage.getItem("garnacha_token") : null;
  if (tok) {
    c.headers = c.headers || {};
    c.headers.Authorization = `Bearer ${tok}`;
  }
  return c;
});

export default function WelcomeEditor() {
  const { user, refresh } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user?.welcome_message || "");
  const [saving, setSaving] = useState(false);

  if (!user) return null;
  const locked = user.welcome_locked && user.role !== "super_admin";

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/welcome/me", { welcome_message: value });
      toast.success("Welcome message updated");
      await refresh();
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <div className="card-soft p-5 mb-6" data-testid="welcome-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="brand-overline">Your Welcome Message</div>
          {!editing ? (
            <div className="font-display font-semibold text-xl text-[#222] mt-1 italic">
              {(user.welcome_message || "—")}
            </div>
          ) : (
            <Textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={3}
              className="mt-2 font-display text-lg"
              data-testid="welcome-textarea"
            />
          )}
          <div className="text-xs text-[#6C757D] mt-1">
            Shown as a full-screen modal at login. {locked
              ? "Your message is locked — only a Super Admin can change it."
              : "You can edit it any time."}
          </div>
        </div>
        {!editing ? (
          <Button
            variant="outline"
            disabled={locked}
            onClick={() => { setValue(user.welcome_message || ""); setEditing(true); }}
            data-testid="edit-welcome-btn"
          >
            <Edit3 size={16} className="mr-2" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setEditing(false)} data-testid="cancel-welcome">
              <X size={16} className="mr-2" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-welcome">
              <Save size={16} className="mr-2" /> Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
